import React, { useRef, useEffect } from 'react';

import application from 'application';

import elementTypes from '../../elements/types';

import { ReactComponent as URLEmbedActionButtonIcon } from 'assets/images/url-embed-action-button-icon.svg';
import { ReactComponent as DividerActionButtonIcon } from 'assets/images/divider-action-button-icon.svg';
import { ReactComponent as HeadingActionButtonIcon } from 'assets/images/heading-action-button-icon.svg';
import { ReactComponent as TextActionButtonIcon } from 'assets/images/text-action-button-icon.svg';
import { ReactComponent as MediaActionButtonIcon } from 'assets/images/media-action-button-icon.svg';
import { ReactComponent as ButtonBlockActionButtonIcon } from 'assets/images/button-block-action-button-icon.svg';
import { ReactComponent as BulletListIcon } from 'assets/images/bullet-list-toggle-icon.svg';
import { ReactComponent as NumberListIcon } from 'assets/images/number-list-toggle-icon.svg';
import { ReactComponent as CheckListIcon } from 'assets/images/check-list-toggle-icon.svg';
import { ReactComponent as QuoteTextIcon } from 'assets/images/quote-text-toggle-icon.svg';
import { ReactComponent as PollIcon } from 'assets/images/poll-icon.svg';
import { ReactComponent as EventIcon } from 'assets/images/event-icon.svg';
import { ReactComponent as LiveStreamingIcon } from 'assets/images/live-streaming-icon.svg';
import { ReactComponent as AttachmentIcon } from 'assets/images/attachment-icon.svg';
import { ReactComponent as FormIcon } from 'assets/images/form-icon.svg';
import { ReactComponent as ImportIcon } from 'assets/images/import-icon.svg';
import { ReactComponent as SourceCodeRegionIcon } from 'assets/images/source-code-region-icon.svg';
import { ReactComponent as MultipleColumnsIcon } from 'assets/images/multiple-columns-icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/delete-node-icon.svg';
import { ReactComponent as TableOfContentsIcon } from 'assets/images/table-of-contents-icon.svg';
import { ReactComponent as TableIcon } from 'assets/images/table-icon.svg';
import { ReactComponent as AddProductIcon } from 'assets/images/products-menu-icon.svg';
import { ReactComponent as AskAIIcon } from 'assets/images/ask-ai-large-icon.svg';
import { ReactComponent as ToggleListItemIcon } from 'assets/images/pages-hidden-indicator-icon.svg';
import { ReactComponent as SignatureIcon } from 'assets/images/signature-icon.svg';
import { ReactComponent as LastUpdatedIcon } from 'assets/images/last-updated-icon.svg';
import { ReactComponent as GalleryIcon } from 'assets/images/gallery-icon.svg';
import { ReactComponent as CarouselIcon } from 'assets/images/carousel-icon.svg';

import { renderBlockIcon } from './block-icons';

import './index.scss';

function EditorActionButton(props) {

  const { label, description, onClick } = props;

  const buttonReference = useRef(null);

  useEffect(function onButtonReferenceChange() {

    if (props.selected !== true) {
      return;
    }

    const buttonElement = buttonReference.current;

    if (buttonElement === null) {
      return;
    }

    buttonElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonReference.current, props.selected]);

  function handleUnfavouriteButtonClick(event) {

    event.stopPropagation();

    const favouritePageNodeId = props._id;

    application.favouritePageNodesManager.delete(favouritePageNodeId);
    props.onRemove(favouritePageNodeId);

    return false;

  }

  function handleUnpublicButtonClick(event) {

    event.stopPropagation();

    const favouritePageNodeId = props._id;

    application.favouritePageNodesManager.unpublic(favouritePageNodeId);
    props.onRemove(favouritePageNodeId);

    return false;

  }

  function handleButtonClick() {

    onClick(props);

  }

  function getButtonType() {

    if (typeof props.nodeType === 'string') {
      return props.nodeType;
    }

    return props.type;

  }

  function isHeadingNode(buttonType) {

    return [
      elementTypes.HEADING_ONE,
      elementTypes.HEADING_TWO,
      elementTypes.HEADING_THREE,
      elementTypes.HEADING_FOUR,
      elementTypes.HEADING_FIVE,
      elementTypes.HEADING_SIX,
    ].indexOf(buttonType) !== -1;

  }

  function renderEditorActionButtonIcon() {

    const buttonType = getButtonType();
    const blockIcon = renderBlockIcon(buttonType);

    if (blockIcon !== null) {
      return blockIcon;
    }

    if (buttonType === elementTypes.URL_EMBED) {
      return <URLEmbedActionButtonIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.DIVIDER) {
      return <DividerActionButtonIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.DEFAULT) {
      return <TextActionButtonIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.HT_FILE) {
      return <MediaActionButtonIcon className='action-icon' />;
    }

    if (isHeadingNode(buttonType) === true) {
      return <HeadingActionButtonIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.BUTTON) {
      return <ButtonBlockActionButtonIcon className="button-block-action-icon" />;
    }

    if (buttonType === elementTypes.AUTHENTICATOR_BUTTON) {
      return <ButtonBlockActionButtonIcon className="button-block-action-icon" />;
    }

    if (buttonType === elementTypes.BULLETED_LIST) {
      return <BulletListIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.NUMBERED_LIST) {
      return <NumberListIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.CHECK_LIST_ITEM) {
      return <CheckListIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.BLOCK_QUOTE) {
      return <QuoteTextIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.POLL_EDITOR_VIEW) {
      return <PollIcon className='media-action-icon' />;
    }

    if (buttonType === elementTypes.SINGLE_PRODUCER_MEETING) {
      return <LiveStreamingIcon className='action-icon' />;
    }

    if (buttonType === elementTypes.HT_FILE) {
      return <AttachmentIcon className='media-action-icon' />;
    }

    if (buttonType === elementTypes.HT_STARTUP_EVENT) {
      return <EventIcon className="event-action-icon" />;
    }

    if (buttonType === elementTypes.FORM || buttonType === elementTypes.SIGNUP_FORM) {
      return <FormIcon className="event-action-icon" />;
    }

    if (buttonType === elementTypes.HTML_IMPORTER) {
      return <ImportIcon className="import-html-icon" />;
    }

    if (buttonType === elementTypes.SOURCE_CODE_REGION) {
      return <SourceCodeRegionIcon className="source-code-region-icon" />;
    }

    if (buttonType === elementTypes.COLUMNS) {
      return <MultipleColumnsIcon className="multiple-columns-icon" />;
    }

    if (buttonType === elementTypes.TABLE_OF_CONTENTS) {
      return <TableOfContentsIcon className="table-of-contents-icon" />;
    }

    if (buttonType === elementTypes.TABLE) {
      return <TableIcon className="table-icon" />;
    }

    if (buttonType === elementTypes.PRODUCT) {
      return <AddProductIcon className="product-icon" />;
    }

    if (buttonType === 'ask-ai') {
      return <AskAIIcon className="ask-ai-icon" />;
    }

    if (buttonType === 'template') {
      return <MultipleColumnsIcon className="multiple-columns-icon" />;
    }

    if (buttonType === elementTypes.TOGGLE_LIST_ITEM) {
      return <ToggleListItemIcon className="toggle-list-item-icon" />;
    }

    if (buttonType === elementTypes.SIGNATURE_PANEL) {
      return <SignatureIcon className="signature-icon" />;
    }

    if (buttonType === elementTypes.LAST_UPDATED) {
      return <LastUpdatedIcon className="last-updated-icon" />;
    }

    if (buttonType === elementTypes.PAGE_AUTHOR) {
      return <SignatureIcon className="signature-icon" />;
    }

    if (buttonType === elementTypes.AI_ASSISTANT) {
      return <AskAIIcon className="ask-ai-icon" />;
    }

    if (buttonType === elementTypes.GALLERY) {
      return <GalleryIcon className="gallery-icon" />;
    }

    if (buttonType === elementTypes.CAROUSEL) {
      return <CarouselIcon className="carousel-icon" />;
    }

  }

  function tryRenderUnfavouriteButton() {

    if (props.type !== 'favouritePageNode') {
      return;
    }
    if (props.startupId !== application.getCurrentStartup()._id) {
      return;
    }
    const unfavrouiteNodeButtonAttributes = {
      type: 'button',
      className: 'unfavourite-page-node-button',
      onClick: handleUnfavouriteButtonClick
    };

    return <button {...unfavrouiteNodeButtonAttributes}>
      <DeleteIcon />
    </button>;

  }

  function tryRenderUnpublicButton() {

    if (props.type !== 'favouritePageNode') {
      return;
    }
    if (props.public === false) {
      return;
    }
    if (application.getCurrentStartup()._id !== import.meta.env.REACT_APP_VENTURZ_ID) {
      return;
    }
    if (props.startupId === import.meta.env.REACT_APP_VENTURZ_ID) {
      return;
    }
    const unfavrouiteNodeButtonAttributes = {
      type: 'button',
      className: 'unfavourite-page-node-button',
      onClick: handleUnpublicButtonClick
    };

    return <button {...unfavrouiteNodeButtonAttributes}>
      <DeleteIcon />
    </button>;

  }

  function renderMain() {

    const editorActionButtonAttributes = {
      className: 'editor-action-button-main',
      ref: buttonReference,
      onClick: handleButtonClick
    };

    if (props.selected === true) {
      editorActionButtonAttributes.className += ' selected';
    }

    return <div {...editorActionButtonAttributes}>

      <div className="editor-action-button-icon-wrapper">
        {renderEditorActionButtonIcon()}
      </div>

      <div className="editor-action-button-data-wrapper">
        <label className="action-button-label ellipsis">{label}</label>
        <p className="action-button-description ellipsis">{description}</p>
        {tryRenderUnfavouriteButton()}
        {tryRenderUnpublicButton()}
      </div>

    </div>;
  }

  return renderMain();

}

export function FileUploadActionButton(props) {

  const { label, description, onFile } = props;

  const fileInputReference = useRef(null);
  const buttonReference = useRef(null);

  useEffect(function onButtonReferenceChange() {

    if (props.selected !== true) {
      return;
    }

    const buttonElement = buttonReference.current;

    if (buttonElement === null) {
      return;
    }

    buttonElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonReference.current, props.selected]);

  useEffect(function onButtonReferenceChange() {

    if (props.applied !== true) {
      return;
    }

    const fileElement = fileInputReference.current;

    if (fileElement === null) {
      return;
    }

    fileElement.click();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileInputReference.current, props.applied]);

  function handleButtonClick() {
    fileInputReference.current.click();

    // onClick();

  }

  function renderEditorActionButtonIcon() {

    return <MediaActionButtonIcon />;

  }

  function renderMain() {

    const editorActionButtonAttributes = {
      className: 'editor-action-button-main media-upload-button'
    };

    const fileInputAttributes = {
      type: 'file',
      className: 'file-upload-control',
      value: '',
      ref: fileInputReference,
      onChange(event) {
        onFile(event.target.files[0]);
      }
    };

    const filePlaceholderAttributes = {
      className: 'editor-file-input-placeholder',
      onClick: handleButtonClick
    };

    if (props.selected === true) {
      editorActionButtonAttributes.className += ' selected';
    }

    return <div {...editorActionButtonAttributes}>

      <div className="editor-action-button-icon-wrapper">
        {renderEditorActionButtonIcon()}
        <input {...fileInputAttributes} />
        <span {...filePlaceholderAttributes}></span>
      </div>

      <div className="editor-action-button-data-wrapper">
        <label className="action-button-label ellipsis">{label}</label>
        <p className="action-button-description ellipsis">{description}</p>
      </div>

    </div>;
  }

  return renderMain();

}

export default EditorActionButton;